import { useEffect } from "react";
import { businessInfo } from "@/data/businessInfo";

const SITE_NAME = businessInfo.name;
const SITE_URL = businessInfo.url;
const DEFAULT_OG_IMAGE = businessInfo.ogImage;
const DEFAULT_DESCRIPTION = `${SITE_NAME}: AAT Licensed accountants near Salisbury, serving Wiltshire, the New Forest and Dorset. Bookkeeping, tax, payroll and business advisory.`;

export interface DocumentMetaOptions {
  /** og:type — "website" for most pages, "article" for blog posts. */
  ogType?: string;
  /** Absolute https URL of the social preview image. */
  ogImage?: string;
  noindex?: boolean;
  /** ISO timestamps, article pages only. */
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  /** Override the canonical path (defaults to the current route). */
  canonicalPath?: string;
}

function setMetaTag(attribute: "name" | "property", key: string, content: string) {
  let el = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMetaTag(attribute: "name" | "property", key: string) {
  document.querySelector(`meta[${attribute}="${key}"]`)?.remove();
}

/**
 * Sets title, description, canonical and the full OpenGraph / Twitter Card set
 * for a route. Every public page calls this, so social tags stay consistent:
 * og:title / twitter:title always mirror the final SEO title, og:description /
 * twitter:description always mirror the final meta description, and og:url and
 * canonical always self-reference the current route in its slash-free form.
 */
export function useDocumentMeta(
  title: string,
  description?: string,
  noindexOrOptions?: boolean | DocumentMetaOptions,
  ogTypeArg: string = "website",
  ogImageArg?: string,
) {
  const opts: DocumentMetaOptions =
    typeof noindexOrOptions === "object"
      ? noindexOrOptions
      : { noindex: noindexOrOptions === true, ogType: ogTypeArg, ogImage: ogImageArg };



  const {
    ogType = "website",
    ogImage,
    noindex,
    publishedTime,
    modifiedTime,
    author,
    canonicalPath,
  } = opts;

  useEffect(() => {
    document.title = title;

    const desc = description || DEFAULT_DESCRIPTION;
    const image = ogImage || DEFAULT_OG_IMAGE;

    setMetaTag("name", "description", desc);

    // Open Graph
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", desc);
    setMetaTag("property", "og:type", ogType);
    setMetaTag("property", "og:site_name", SITE_NAME);
    setMetaTag("property", "og:locale", "en_GB");
    setMetaTag("property", "og:image", image);
    setMetaTag("property", "og:image:alt", `${title} — ${SITE_NAME}`);

    // Twitter Card
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", desc);
    setMetaTag("name", "twitter:image", image);
    setMetaTag("name", "twitter:image:alt", `${title} — ${SITE_NAME}`);

    // Article-only tags
    if (ogType === "article") {
      if (publishedTime) setMetaTag("property", "article:published_time", publishedTime);
      if (modifiedTime) setMetaTag("property", "article:modified_time", modifiedTime);
      if (author) setMetaTag("property", "article:author", author);
      setMetaTag("property", "article:publisher", SITE_URL);
    } else {
      removeMetaTag("property", "article:published_time");
      removeMetaTag("property", "article:modified_time");
      removeMetaTag("property", "article:author");
      removeMetaTag("property", "article:publisher");
    }

    // Robots noindex
    if (noindex) {
      setMetaTag("name", "robots", "noindex, nofollow");
    } else {
      removeMetaTag("name", "robots");
    }

    // Canonical URL — always the slash-free production form so /about and
    // /about/ consolidate into a single indexed URL, and og:url matches it.
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const rawPath = canonicalPath ?? window.location.pathname;
    const path = rawPath.replace(/\/+$/, "");
    const canonicalHref = SITE_URL + (path || "/");
    canonical.setAttribute("href", canonicalHref);
    setMetaTag("property", "og:url", canonicalHref);

    return () => {
      document.title = SITE_NAME;
      if (noindex) removeMetaTag("name", "robots");
    };
  }, [title, description, noindex, ogType, ogImage, publishedTime, modifiedTime, author, canonicalPath]);
}
